import React, { useEffect, useState } from 'react';
import { CheckCircle, Loader, X, ExternalLink, Zap, Send, AlertTriangle } from 'lucide-react';
import api from '../api';

// ─── SVG Logos ───────────────────────────────────────────────────────────────
const GoogleLogo = () => (
  <svg width="24" height="24" viewBox="0 0 48 48">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
  </svg>
);

const TelegramLogo = () => (
  <svg width="24" height="24" viewBox="0 0 48 48">
    <circle cx="24" cy="24" r="24" fill="#229ED9"/>
    <path fill="white" d="M10.2 23.4l5.8 2.2 2.2 7 2.8-2.8 5.8 4.4 8-16.8-24.6 6zM18 25.2l10-6.4-6.4 7.2-.2 3.4-3.4-4.2z"/>
  </svg>
);

const WhatsAppLogo = () => (
  <svg width="24" height="24" viewBox="0 0 48 48">
    <circle cx="24" cy="24" r="24" fill="#25D366"/>
    <path fill="white" d="M34.5 13.5C32 11 28.6 9.5 24.9 9.5c-7.7 0-14 6.3-14 14 0 2.5.6 4.9 1.9 7l-2 7.3 7.5-2c2 1.1 4.3 1.7 6.6 1.7 7.7 0 14-6.3 14-14 0-3.7-1.5-7.2-4.4-10zm-9.6 21.5c-2.1 0-4.2-.6-6-1.6l-.4-.3-4.4 1.2 1.2-4.3-.3-.4c-1.1-1.8-1.7-3.9-1.7-6.1 0-6.2 5.1-11.3 11.3-11.3 3 0 5.8 1.2 7.9 3.3 2.1 2.1 3.3 4.9 3.3 7.9 0 6.3-5.1 11.6-11.3 11.6z"/>
  </svg>
);

const SlackLogo = () => (
  <svg width="24" height="24" viewBox="0 0 48 48">
    <path fill="#E01E5A" d="M10 28.5a3.5 3.5 0 01-3.5 3.5A3.5 3.5 0 013 28.5 3.5 3.5 0 016.5 25H10v3.5zm1.8 0a3.5 3.5 0 013.5-3.5 3.5 3.5 0 013.5 3.5v8.7A3.5 3.5 0 0115.3 41a3.5 3.5 0 01-3.5-3.5v-9z"/>
    <path fill="#36C5F0" d="M15.3 10a3.5 3.5 0 01-3.5-3.5A3.5 3.5 0 0115.3 3a3.5 3.5 0 013.5 3.5V10h-3.5zm0 1.8a3.5 3.5 0 013.5 3.5 3.5 3.5 0 01-3.5 3.5H6.5A3.5 3.5 0 013 15.3a3.5 3.5 0 013.5-3.5h8.8z"/>
    <path fill="#2EB67D" d="M33.7 15.3a3.5 3.5 0 013.5-3.5A3.5 3.5 0 0141 15.3a3.5 3.5 0 01-3.5 3.5H33.7v-3.5zm-1.8 0a3.5 3.5 0 01-3.5 3.5 3.5 3.5 0 01-3.5-3.5V6.5A3.5 3.5 0 0128.4 3a3.5 3.5 0 013.5 3.5v8.8z"/>
    <path fill="#ECB22E" d="M28.4 33.7a3.5 3.5 0 013.5 3.5A3.5 3.5 0 0128.4 41a3.5 3.5 0 01-3.5-3.5V33.7h3.5zm0-1.8a3.5 3.5 0 01-3.5-3.5 3.5 3.5 0 013.5-3.5h8.8A3.5 3.5 0 0141 28.4a3.5 3.5 0 01-3.5 3.5h-9.1z"/>
  </svg>
);

const EMAIL_SENDERS = [
  { id: 'sendgrid', name: 'SendGrid', desc: 'Transactional & marketing emails', docsUrl: 'https://app.sendgrid.com/settings/api_keys', fields: [{ key: 'api_key', label: 'API Key', type: 'password' }, { key: 'from_email', label: 'From Email', type: 'email' }], color: '#1A82E2', initials: 'SG' },
  { id: 'mailgun', name: 'Mailgun', desc: 'Developer-focused email delivery', docsUrl: 'https://app.mailgun.com/settings/api_security', fields: [{ key: 'api_key', label: 'API Key', type: 'password' }, { key: 'domain', label: 'Sending Domain', type: 'text' }, { key: 'from_email', label: 'From Email', type: 'email' }], color: '#F06B26', initials: 'MG' },
  { id: 'zoho', name: 'Zoho Mail', desc: 'Business email by Zoho', docsUrl: 'https://mail.zoho.com', fields: [{ key: 'api_key', label: 'API Key', type: 'password' }, { key: 'from_email', label: 'From Email', type: 'email' }], color: '#E42527', initials: 'ZO' },
  { id: 'brevo', name: 'Brevo', desc: 'All-in-one marketing platform', docsUrl: 'https://app.brevo.com/settings/keys/api', fields: [{ key: 'api_key', label: 'API Key', type: 'password' }, { key: 'from_email', label: 'From Email', type: 'email' }], color: '#0B996E', initials: 'BR' },
  { id: 'mailchimp', name: 'Mailchimp', desc: 'Email marketing automation', docsUrl: 'https://us1.admin.mailchimp.com/account/api/', fields: [{ key: 'api_key', label: 'API Key', type: 'password' }, { key: 'from_email', label: 'From Email', type: 'email' }], color: '#FFE01B', textColor: '#000', initials: 'MC' },
  { id: 'postmark', name: 'Postmark', desc: 'Fast transactional delivery', docsUrl: 'https://account.postmarkapp.com/api_tokens', fields: [{ key: 'api_key', label: 'Server API Token', type: 'password' }, { key: 'from_email', label: 'From Email', type: 'email' }], color: '#FFBB00', textColor: '#000', initials: 'PM' },
  { id: 'resend', name: 'Resend', desc: 'Modern email API', docsUrl: 'https://resend.com/api-keys', fields: [{ key: 'api_key', label: 'API Key', type: 'password' }, { key: 'from_email', label: 'From Email', type: 'email' }], color: '#000000', initials: 'RE' },
  { id: 'smtp', name: 'Custom SMTP', desc: 'Hostinger, Gmail SMTP, etc.', docsUrl: '', fields: [{ key: 'smtp_host', label: 'SMTP Host', type: 'text' }, { key: 'smtp_port', label: 'SMTP Port', type: 'text' }, { key: 'smtp_user', label: 'Username / Email', type: 'text' }, { key: 'smtp_password', label: 'Password', type: 'password' }, { key: 'from_email', label: 'From Email', type: 'email' }], color: '#6366f1', initials: 'SMTP' },
];

const OTHER = [
  { id: 'slack', name: 'Slack', desc: 'Channel & DM notifications', logo: <SlackLogo />, soon: false },
  { id: 'whatsapp', name: 'WhatsApp Business', desc: 'Customer messaging', logo: <WhatsAppLogo />, soon: true },
  { id: 'notion', name: 'Notion', desc: 'Pages & databases', initials: 'N', color: '#fff', textColor: '#000', soon: true },
  { id: 'airtable', name: 'Airtable', desc: 'Base & table sync', initials: 'A', color: '#FF6B35', soon: true },
  { id: 'stripe', name: 'Stripe', desc: 'Payment event triggers', initials: 'S', color: '#635BFF', soon: true },
  { id: 'hubspot', name: 'HubSpot CRM', desc: 'Contacts & deals', initials: 'H', color: '#FF7A59', soon: true },
  { id: 'typeform', name: 'Typeform', desc: 'Form submission triggers', initials: 'TF', color: '#262627', soon: true },
  { id: 'calendly', name: 'Calendly', desc: 'Booking triggers', initials: 'C', color: '#006BFF', soon: true },
  { id: 'openai', name: 'OpenAI / GPT', desc: 'AI inside workflows', initials: 'AI', color: '#10a37f', soon: true },
  { id: 'shopify', name: 'Shopify', desc: 'Order & customer events', initials: 'SH', color: '#96BF48', soon: true },
];

// ─── Telegram Modal ───────────────────────────────────────────────────────────
function TelegramModal({ onClose, onConnected }) {
  const [token, setToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);

  const connect = async () => {
    if (!token.trim()) { setError('Bot token is required'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.post('/integrations/telegram/connect', { bot_token: token.trim(), chat_id: chatId.trim() });
      onConnected(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid token. Check and try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', width: '100%', maxWidth: 460, padding: 26 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TelegramLogo />
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Connect Telegram</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>Step {step} of 2</div>
            </div>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-icon"><X size={16} /></button>
        </div>

        {step === 1 && (
          <>
            <div style={{ padding: '14px 16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 20, lineHeight: 1.9 }}>
              <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>How to get your bot token:</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>1. Open Telegram → search <strong style={{ color: 'var(--accent3)' }}>@BotFather</strong></div>
              <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>2. Send <strong style={{ color: 'var(--accent3)' }}>/newbot</strong> and follow the steps</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>3. Copy the token BotFather gives you</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>4. Paste it below</div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label>Bot Token</label>
              <input value={token} onChange={e => setToken(e.target.value)} placeholder="7234567890:AAFxxxxxxxxxxxxxxxxxxxxxxxx" style={{ fontFamily: 'monospace', fontSize: 12 }} />
            </div>
            {error && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 14, padding: '10px 14px', background: 'var(--red-bg)', borderRadius: 8, fontWeight: 600 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center', fontWeight: 700 }} onClick={() => token.trim() ? setStep(2) : setError('Paste your bot token first')}>
                Next →
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div style={{ padding: '14px 16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 20, lineHeight: 1.9 }}>
              <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>Get your Chat ID (optional):</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>1. Message your bot on Telegram</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>2. Open: <span style={{ color: 'var(--accent3)', fontFamily: 'monospace', fontSize: 11 }}>api.telegram.org/bot{'{TOKEN}'}/getUpdates</span></div>
              <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>3. Copy the <strong>chat.id</strong> number</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4, fontWeight: 500 }}>You can skip this and add it later.</div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label>Your Chat ID (optional)</label>
              <input value={chatId} onChange={e => setChatId(e.target.value)} placeholder="e.g. 987654321 or -1001234567890" />
              {chatId && <p style={{ fontSize: 12, color: 'var(--green)', marginTop: 5, fontWeight: 600 }}>✓ We'll send a test message to confirm</p>}
            </div>
            {error && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 14, padding: '10px 14px', background: 'var(--red-bg)', borderRadius: 8, fontWeight: 600 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setStep(1)}>← Back</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center', fontWeight: 700 }} onClick={connect} disabled={loading}>
                {loading ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Connecting...</> : <><CheckCircle size={14} /> Connect Telegram</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Email Modal ──────────────────────────────────────────────────────────────
function EmailModal({ sender, onClose, onConnected }) {
  const initForm = () => {
    const f = { api_key: '', domain: '', from_email: '', smtp_host: '', smtp_port: '587', smtp_user: '', smtp_password: '' };
    return f;
  };
  const [form, setForm] = useState(initForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const connect = async () => {
    setLoading(true); setError('');
    try {
      await api.post('/integrations/email/connect', {
        platform: sender.id,
        api_key: form.api_key || '',
        domain: form.domain || '',
        from_email: form.from_email || '',
        smtp_host: form.smtp_host || '',
        smtp_port: parseInt(form.smtp_port) || 587,
        smtp_user: form.smtp_user || '',
        smtp_password: form.smtp_password || '',
      });
      onConnected(sender.id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Connection failed. Check your credentials.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', width: '100%', maxWidth: 420, padding: 26, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 9, background: sender.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, color: sender.textColor || 'white', flexShrink: 0 }}>
              {sender.initials}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800 }}>Connect {sender.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>{sender.desc}</div>
            </div>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-icon"><X size={16} /></button>
        </div>

        {sender.docsUrl && (
          <a href={sender.docsUrl} target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--accent3)', marginBottom: 18, textDecoration: 'none', fontWeight: 700 }}>
            <ExternalLink size={13} /> Get your API key from {sender.name} →
          </a>
        )}

        {sender.fields.map(field => (
          <div key={field.key} style={{ marginBottom: 14 }}>
            <label>{field.label}</label>
            <input
              type={field.type}
              value={form[field.key]}
              onChange={e => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
              placeholder={
                field.key === 'smtp_port' ? '587' :
                field.key === 'from_email' ? 'noreply@yourdomain.com' :
                field.key === 'domain' ? 'yourdomain.com' :
                field.key === 'smtp_host' ? 'smtp.hostinger.com' : ''
              }
            />
          </div>
        ))}

        {error && (
          <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 16, padding: '10px 14px', background: 'var(--red-bg)', borderRadius: 8, fontWeight: 600 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center', fontWeight: 700 }} onClick={connect} disabled={loading}>
            {loading ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</> : `Connect ${sender.name}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Integrations() {
  const [googleStatus, setGoogleStatus] = useState({ connected: false, email: null });
  const [telegramStatus, setTelegramStatus] = useState({ connected: false });
  const [emailStatus, setEmailStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [successMsg, setSuccessMsg] = useState('');

  const load = async () => {
    try {
      const [gRes, tRes, eRes] = await Promise.all([
        api.get('/oauth/google/status'),
        api.get('/integrations/telegram/status'),
        api.get('/integrations/email/status'),
      ]);
      setGoogleStatus(gRes.data);
      setTelegramStatus(tRes.data);
      setEmailStatus(eRes.data || {});
    } catch (e) {
      console.error('Load error:', e);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // Handle OAuth callback — restore token if passed in URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const success = params.get('success');
    const error = params.get('error');

    if (token) {
      // Restore session from OAuth callback
      localStorage.setItem('token', token);
      // Refresh user data
      api.get('/auth/me').then(r => {
        localStorage.setItem('user', JSON.stringify(r.data));
      }).catch(() => {});
    }

    if (success === 'google') {
      setSuccessMsg('✅ Google account connected successfully!');
      setTimeout(() => setSuccessMsg(''), 5000);
    }

    if (error) {
      setSuccessMsg(error === 'cancelled' ? '' : '❌ Connection failed. Please try again.');
      setTimeout(() => setSuccessMsg(''), 5000);
    }

    // Clean URL
    if (success || error || token) {
      window.history.replaceState({}, '', '/integrations');
    }

    load();
  }, []);

  const connectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      const res = await api.get('/oauth/google/connect');
      window.location.href = res.data.url;
    } catch {
      alert('Failed to start connection. Try again.');
      setConnectingGoogle(false);
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google? Your Google-based automations will stop working.')) return;
    try {
      const creds = await api.get('/credentials');
      await Promise.all(
        creds.data
          .filter(c => ['gmail','googleSheets','googleCalendar','googleDrive'].includes(c.platform))
          .map(c => api.delete(`/credentials/${c.id}`))
      );
      setGoogleStatus({ connected: false, email: null });
    } catch { alert('Failed to disconnect'); }
  };

  const disconnectTelegram = async () => {
    try {
      await api.delete('/integrations/telegram/disconnect');
      setTelegramStatus({ connected: false });
    } catch { alert('Failed'); }
  };

  const disconnectEmail = async (id) => {
    try {
      await api.delete(`/integrations/email/disconnect/${id}`);
      setEmailStatus(prev => { const n = { ...prev }; delete n[id]; return n; });
    } catch { alert('Failed'); }
  };

  const connectedCount = [
    googleStatus.connected,
    telegramStatus.connected,
    ...Object.values(emailStatus).map(e => e?.connected)
  ].filter(Boolean).length;

  const categories = ['All', 'Google', 'Messaging', 'Email Sending', 'Coming Soon'];

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <Loader size={24} color="var(--accent)" style={{ animation: 'spin 1s linear infinite' }} />
    </div>
  );

  return (
    <div className="fade-in">
      {showTelegramModal && (
        <TelegramModal
          onClose={() => setShowTelegramModal(false)}
          onConnected={info => { setTelegramStatus({ connected: true, ...info }); setShowTelegramModal(false); setSuccessMsg('✅ Telegram connected!'); setTimeout(() => setSuccessMsg(''), 4000); }}
        />
      )}
      {showEmailModal && (
        <EmailModal
          sender={showEmailModal}
          onClose={() => setShowEmailModal(null)}
          onConnected={id => { setEmailStatus(prev => ({ ...prev, [id]: { connected: true } })); setShowEmailModal(null); setSuccessMsg(`✅ ${showEmailModal.name} connected!`); setTimeout(() => setSuccessMsg(''), 4000); }}
        />
      )}

      {/* Success/error banner */}
      {successMsg && (
        <div style={{ padding: '12px 18px', borderRadius: 10, marginBottom: 20, fontSize: 14, fontWeight: 700, background: successMsg.startsWith('✅') ? 'var(--green-bg)' : 'var(--red-bg)', color: successMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)', border: `1px solid ${successMsg.startsWith('✅') ? 'var(--green-border)' : 'rgba(239,68,68,0.25)'}` }}>
          {successMsg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 38, marginBottom: 6 }}>CONNECTIONS</h1>
          <p style={{ color: 'var(--text2)', fontSize: 14, fontWeight: 600 }}>Connect your tools so automations can act on your behalf</p>
        </div>
        <div style={{ padding: '8px 16px', background: connectedCount > 0 ? 'var(--green-bg)' : 'var(--bg2)', border: `1px solid ${connectedCount > 0 ? 'var(--green-border)' : 'var(--border)'}`, borderRadius: 20, fontSize: 13, fontWeight: 800, color: connectedCount > 0 ? 'var(--green)' : 'var(--text3)' }}>
          {connectedCount} connected
        </div>
      </div>

      {/* Category tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {categories.map(cat => (
          <button key={cat} onClick={() => setActiveCategory(cat)}
            className={activeCategory === cat ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            style={{ fontWeight: 700 }}>
            {cat}
          </button>
        ))}
      </div>

      {/* ── Google ── */}
      {(activeCategory === 'All' || activeCategory === 'Google') && (
        <div style={{ background: 'var(--bg2)', border: `1.5px solid ${googleStatus.connected ? 'rgba(34,197,94,0.35)' : 'var(--border)'}`, borderRadius: 'var(--radius)', padding: '20px 22px', marginBottom: 10, boxShadow: googleStatus.connected ? '0 0 28px rgba(34,197,94,0.07)' : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', gap: 14, flex: 1 }}>
              <div style={{ width: 48, height: 48, background: 'var(--bg3)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', flexShrink: 0 }}>
                <GoogleLogo />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: 16, fontWeight: 800 }}>Google Suite</span>
                  {googleStatus.connected && <span className="badge badge-active" style={{ fontSize: 10, fontWeight: 800 }}><CheckCircle size={10} /> Connected</span>}
                </div>
                <p style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 600, marginBottom: 10 }}>
                  {googleStatus.connected ? `Signed in as ${googleStatus.email}` : 'One login gives you Gmail, Sheets, Calendar & Drive'}
                </p>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {['Gmail', 'Google Sheets', 'Google Calendar', 'Google Drive'].map(p => (
                    <span key={p} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, fontWeight: 700, background: googleStatus.connected ? 'var(--green-bg)' : 'var(--bg3)', color: googleStatus.connected ? 'var(--green)' : 'var(--text3)', border: `1px solid ${googleStatus.connected ? 'var(--green-border)' : 'var(--border)'}` }}>{p}</span>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ flexShrink: 0 }}>
              {googleStatus.connected
                ? <button className="btn btn-danger btn-sm" style={{ fontWeight: 700 }} onClick={disconnectGoogle}>Disconnect</button>
                : <button className="btn btn-primary btn-sm" style={{ fontWeight: 700 }} onClick={connectGoogle} disabled={connectingGoogle}>
                    {connectingGoogle ? <><Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> Connecting...</> : 'Connect Google'}
                  </button>
              }
            </div>
          </div>
        </div>
      )}

      {/* ── Telegram ── */}
      {(activeCategory === 'All' || activeCategory === 'Messaging') && (
        <div style={{ background: 'var(--bg2)', border: `1.5px solid ${telegramStatus.connected ? 'rgba(34,197,94,0.35)' : 'var(--border)'}`, borderRadius: 'var(--radius)', padding: '20px 22px', marginBottom: 10, boxShadow: telegramStatus.connected ? '0 0 28px rgba(34,197,94,0.07)' : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
              <div style={{ width: 48, height: 48, background: 'rgba(34,158,217,0.12)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(34,158,217,0.25)', flexShrink: 0 }}>
                <TelegramLogo />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 16, fontWeight: 800 }}>Telegram</span>
                  {telegramStatus.connected && <span className="badge badge-active" style={{ fontSize: 10, fontWeight: 800 }}><CheckCircle size={10} /> Connected</span>}
                </div>
                <p style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 600 }}>
                  {telegramStatus.connected ? `Bot: @${telegramStatus.bot_username}` : 'Send automated messages via Telegram bot'}
                </p>
              </div>
            </div>
            {telegramStatus.connected
              ? <button className="btn btn-danger btn-sm" style={{ fontWeight: 700 }} onClick={disconnectTelegram}>Disconnect</button>
              : <button className="btn btn-secondary btn-sm" style={{ fontWeight: 700 }} onClick={() => setShowTelegramModal(true)}>Connect Telegram</button>
            }
          </div>
        </div>
      )}

      {/* ── Email Senders ── */}
      {(activeCategory === 'All' || activeCategory === 'Email Sending') && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, marginTop: 12 }}>
            Email Sending — {Object.keys(emailStatus).length} connected
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {EMAIL_SENDERS.map(sender => {
              const status = emailStatus[sender.id];
              return (
                <div key={sender.id} style={{ background: 'var(--bg2)', border: `1.5px solid ${status?.connected ? 'rgba(34,197,94,0.35)' : 'var(--border)'}`, borderRadius: 'var(--radius)', padding: '16px 18px', transition: 'all 0.2s', boxShadow: status?.connected ? '0 0 16px rgba(34,197,94,0.06)' : 'none' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = status?.connected ? 'rgba(34,197,94,0.5)' : 'var(--border2)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = status?.connected ? 'rgba(34,197,94,0.35)' : 'var(--border)'; e.currentTarget.style.transform = 'none'; }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: sender.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: sender.textColor || 'white', flexShrink: 0 }}>
                      {sender.initials}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>{sender.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>{sender.desc}</div>
                    </div>
                    {status?.connected && <CheckCircle size={16} color="var(--green)" />}
                  </div>
                  {status?.connected
                    ? <button className="btn btn-danger btn-sm" style={{ width: '100%', justifyContent: 'center', fontWeight: 700, fontSize: 12 }} onClick={() => disconnectEmail(sender.id)}>
                        Disconnect
                      </button>
                    : <button className="btn btn-secondary btn-sm" style={{ width: '100%', justifyContent: 'center', fontWeight: 700, fontSize: 12 }} onClick={() => setShowEmailModal(sender)}>
                        <Send size={12} /> Connect
                      </button>
                  }
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Other / Coming Soon ── */}
      {(activeCategory === 'All' || activeCategory === 'Coming Soon' || activeCategory === 'Messaging') && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, marginTop: 12 }}>
            More Integrations
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 9 }}>
            {OTHER.map(p => (
              <div key={p.id} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', opacity: p.soon ? 0.6 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
                  {p.logo
                    ? <div style={{ width: 32, height: 32, flexShrink: 0 }}>{p.logo}</div>
                    : <div style={{ width: 32, height: 32, borderRadius: 7, background: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: p.textColor || 'white', flexShrink: 0 }}>{p.initials}</div>
                  }
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>{p.name}</div>
                  </div>
                  {p.soon && <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 800, color: 'var(--text3)', background: 'var(--bg3)', padding: '2px 7px', borderRadius: 20, border: '1px solid var(--border)', flexShrink: 0 }}>SOON</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>{p.desc}</div>
                {!p.soon && <button className="btn btn-secondary btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 10, fontWeight: 700, fontSize: 12 }}>Connect</button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      <div style={{ marginTop: 20, padding: '18px 22px', background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(168,85,247,0.06))', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Zap size={20} color="var(--accent3)" />
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 2 }}>Need a custom integration?</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 600 }}>We build any platform you need — usually within 48 hours</div>
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" style={{ fontWeight: 700, flexShrink: 0 }} onClick={() => alert('Contact: support@astrautomations.com')}>
          Request Integration
        </button>
      </div>
    </div>
  );
}
